import { CommonModule } from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';

import { IsIntegerPipe } from 'src/app/pipes/is-integer.pipe';

@Component({
    selector: 'app-pagination-controls',
    imports: [
        CommonModule,
        IsIntegerPipe,
    ],
    templateUrl: './pagination-controls.component.html',
    styleUrl: './pagination-controls.component.scss',
	standalone: true
})
export class PaginationControlsComponent implements OnInit {

	public pagesIndices!: number[];

	public get lastPagesIndicesValue(): number {
		return this.pagesIndices[this.pagesIndices.length - 1];
	}

	public initializePageIndices(): void {

		if (this.pagesQuantity === 6) {
			// If we can see `firstPage * * * * lastPage` without hiding in-between items.
			this.pagesIndices = [...Array(Math.min(6, this.pagesQuantity)).keys()];
		} else if (this._activePageIndex < 4) {
			// If active page index is less than 4 then we get this sequence `1 2 3 4 5 ... lastPage`.
			this.pagesIndices = [...Array(Math.min(5, this.pagesQuantity)).keys()];
		} else if (this.pagesQuantity - this.activePageIndex <= 4) {
			// If active page index distance from the "lastPage" index is less than 4 then we get this sequence `firstPage ... * * * * lastPage`.
			this.pagesIndices = [...Array(Math.min(5, this.pagesQuantity)).keys()].map((a) => this.pagesQuantity - 1 - a).reverse();
		} else {
			// If active page index is in-between "firstPage" and "lastPage" without touching the margin of 4 from either side then we get this sequence `firstPage ...  * * * * ... lastPage`.
			this.pagesIndices = [...Array(Math.min(4, this.pagesQuantity)).keys()].map((a) => this.activePageIndex + a);
		}

		if(this.itemsQuantity < 5) {
			this.allowedPageSizes = [];
		} else {
			this.allowedPageSizes = [5, 7, 10];
		}
	}

	private _pagesQuantity!: number;
	public get pagesQuantity(): number {
		return this._pagesQuantity;
	}
	public set pagesQuantity(value: number) {
		this._pagesQuantity = value;

		if (this._activePageIndex >= this._pagesQuantity) {
			console.error("Active page index cannot be bigger or equal to the quantity of pages.");
		}

		// When `this.pagesQuantity` changes - it's very likely that `this._activePageIndex` changes too, so we need to update `this.pageIndices` to reflect UI change.
		this.initializePageIndices();
	}

	@Output()
	public activePageIndexChange: EventEmitter<number> = new EventEmitter<number>();

	private _activePageIndex: number = 0;
	public get activePageIndex(): number {
		return this._activePageIndex;
	}
	@Input()
	public set activePageIndex(value: number) {
		this._activePageIndex = value;

		// If we can't see full range we need to do some manipulations with UI.
		if (this.pagesQuantity > 6) {
			if (this._activePageIndex !== 0 && this._activePageIndex === this.pagesIndices[0]) {
				if (this.pagesIndices[this.pagesIndices.length - 1] === this.pagesQuantity - 1) {
					this.pagesIndices.pop();
				}

				this.pagesIndices = this.pagesIndices.map((index) => index - 1);

				if (this.pagesIndices[0] - 1 === 0) {
					this.pagesIndices.unshift(0);
				}
			} else if (this._activePageIndex !== this.pagesQuantity - 1 && this._activePageIndex === this.pagesIndices[this.pagesIndices.length - 1]) {
				if (this.pagesIndices[0] === 0) {
					this.pagesIndices.shift();
				}

				this.pagesIndices = this.pagesIndices.map((index) => index + 1);

				if (this.pagesIndices[this.pagesIndices.length - 1] + 1 === this.pagesQuantity - 1) {
					this.pagesIndices.push(this.pagesQuantity - 1);
				}
			}

			// if: user clicks on firstPage.
			if (this._activePageIndex === 0 && this.pagesQuantity >= 6) {
				// if: sequence is `firstPage ... * * * * lastPage`.
				if (this.pagesIndices[this.pagesIndices.length - 1] === this.pagesQuantity - 1) {
					this.pagesIndices.pop();
				}

				// if: sequence is not like this `firstPage * * * * ?...? lastPage`.
				if (this.pagesIndices[0] !== 0) {
					for (let a = 0; a < this.pagesIndices.length; a++) {
						this.pagesIndices[a] = 1 + a;
					}

					this.pagesIndices.unshift(0);
				}
			}

			// if: user clicks on lastPage.
			if (this._activePageIndex === this.pagesQuantity - 1) {
				// if: sequence is `firstPage * * * * ... lastPage`.
				if (this.pagesIndices[0] === 0) {
					this.pagesIndices.shift();
				}

				// if: sequence is not like this `firstPage ?...? * * * * lastPage`.
				if (this.pagesIndices[this.pagesIndices.length - 1] !== this.pagesQuantity - 1) {
					for (let a = 0; a < this.pagesIndices.length; a++) {
						this.pagesIndices[a] = this.pagesQuantity - 2 - a;
					}

					this.pagesIndices.reverse();

					this.pagesIndices.push(this.pagesQuantity - 1);
				}
			}
		}

		this.activePageIndexChange.emit(this._activePageIndex);
	}

	@Input()
	public allowedPageSizes: number[] = [5, 7, 10];

	@Output()
	public pageSizeChange: EventEmitter<number> = new EventEmitter<number>();

	private _pageSize: number = 5;
	public get pageSize(): number {
		return this._pageSize;
	}
	@Input()
	public set pageSize(value: number) {
		this._pageSize = value;

		this.pagesQuantity = Math.floor(this.itemsQuantity / this._pageSize) + (this.itemsQuantity % this._pageSize !== 0 ? 1 : 0);

		this.pageSizeChange.emit(this._pageSize);
	}

	private _itemsQuantity!: number;
	public get itemsQuantity(): number {
		return this._itemsQuantity;
	}
	@Input({ required: true })
	public set itemsQuantity(value: number) {
		this._itemsQuantity = value;

		this.pagesQuantity = Math.floor(this.itemsQuantity / this._pageSize) + (this.itemsQuantity % this._pageSize !== 0 ? 1 : 0);
	}

	public switchToPreviousPage() {
		if (this.activePageIndex > 0) {
			this.activePageIndex -= 1;
		}
	}

	public switchToNextPage() {
		if (this.activePageIndex < this.pagesQuantity) {
			this.activePageIndex += 1;
		}
	}

	public switchToPage(pageIndex: number) {
		if (pageIndex >= 0 && pageIndex <= this.pagesQuantity) {
			this.activePageIndex = pageIndex;
		}
	}

	ngOnInit(): void {
		this.initializePageIndices();
	}
}
